import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import SessioneScaduta from "@/components/SessioneScaduta";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import SettingsHub from "@/pages/SettingsHub";
import PassaggioProprietaSettings from "@/pages/PassaggioProprietaSettings";
import ElenchiDatabaseSettings from "@/pages/ElenchiDatabaseSettings";
import DeletionRequests from "@/pages/DeletionRequests";
import Companies from "@/pages/Companies";
import CompanyDetail from "@/pages/CompanyDetail";
import Users from "@/pages/Users";
import UserDetail from "@/pages/UserDetail";
import MigrationReview from "@/pages/MigrationReview";
import BackupPlaceholder from "@/pages/BackupPlaceholder";
import AuditLogPlaceholder from "@/pages/AuditLogPlaceholder";
import Vehicles from "@/pages/Vehicles";
import Web from "@/pages/Web";
import StampeInBianco from "@/pages/StampeInBianco";
import CrmOpportunita from "@/pages/crm/CrmOpportunita";
import Calendario from "@/pages/calendario/Calendario";
import CrmOpportunitaScheda from "@/pages/crm/CrmOpportunitaScheda";
import CrmContatti from "@/pages/crm/CrmContatti";
import CrmContattoScheda from "@/pages/crm/CrmContattoScheda";
import CrmImpostazioni from "@/pages/crm/CrmImpostazioni";
import CrmDashboard from "@/pages/crm/CrmDashboard";
import VehicleDetail from "@/pages/VehicleDetail";
import VehicleCestino from "@/pages/VehicleCestino";
import Anagrafiche from "@/pages/Anagrafiche";
import AnagraficaDetail from "@/pages/AnagraficaDetail";
import AnagraficaCestino from "@/pages/AnagraficaCestino";
import Vendite from "@/pages/Vendite";
import VenditaPage from "@/pages/VenditaPage";
import AppLayout from "@/layouts/AppLayout";

import ChangePassword from "@/pages/ChangePassword";
import { useLocation } from "react-router-dom";
import { FLAG_PASSAGGIO, haFlag, vedeImpostazioni } from "@/lib/ambito";

const ELEVATED = ["developer", "admin"];

function isElevated(user) {
  if (!user) return false;
  const r = user.role_default || (user.role || "").toLowerCase();
  return ELEVATED.includes(r);
}

function Private({ children, requireElevated, consenti }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // Sprint UTENTI-1: redirect forzato a /change-password se must_change_password.
  // Eccezione: la pagina /change-password stessa è accessibile (no loop).
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (requireElevated && !isElevated(user)) return <Navigate to="/" replace />;
  if (consenti && !consenti(user)) return <Navigate to="/" replace />;  // Sprint CRM-5 — voce di Impostazioni non utilizzabile
  return children;
}

// Sprint 2D-PART-B: backward-compat redirect /suppliers/:id e /customers/:id → /anagrafiche/:id
// (UUID conservato dalla migrazione)
function RedirectToAnagrafica() {
  const id = window.location.pathname.split("/").pop();
  return <Navigate to={`/anagrafiche/${id}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <SessioneScaduta />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<Private><ChangePassword /></Private>} />
            <Route element={<Private><AppLayout /></Private>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/pratiche" element={<Navigate to="/vendite?tipo=pratica" replace />} />
              <Route path="/vendite" element={<Vendite />} />
              <Route path="/vendite/:id" element={<VenditaPage />} />
              <Route path="/preventivi" element={<Navigate to="/vendite?tipo=preventivo" replace />} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/vehicles/cestino" element={<Private requireElevated><VehicleCestino /></Private>} />
              <Route path="/vehicles/new" element={<VehicleDetail />} />
              <Route path="/vehicles/:id" element={<VehicleDetail />} />
              <Route path="/web" element={<Web />} />
              <Route path="/stampe" element={<StampeInBianco />} />
              <Route path="/calendario" element={<Calendario />} />
              <Route path="/crm" element={<CrmOpportunita />} />
              <Route path="/crm/opportunita/:id" element={<CrmOpportunitaScheda />} />
              <Route path="/crm/contatti" element={<CrmContatti />} />
              <Route path="/crm/contatti/:id" element={<CrmContattoScheda />} />
              <Route path="/crm/impostazioni" element={<CrmImpostazioni />} />
              <Route path="/crm/dashboard" element={<CrmDashboard />} />
              {/* Sprint 2D-PART-B: redirect legacy → /anagrafiche */}
              <Route path="/suppliers" element={<Navigate to="/anagrafiche?categoria=fornitore_veicoli" replace />} />
              <Route path="/suppliers/cestino" element={<Navigate to="/anagrafiche/cestino" replace />} />
              <Route path="/suppliers/:id" element={<RedirectToAnagrafica />} />
              <Route path="/customers" element={<Navigate to="/anagrafiche?categoria=base" replace />} />
              <Route path="/customers/cestino" element={<Navigate to="/anagrafiche/cestino" replace />} />
              <Route path="/customers/:id" element={<RedirectToAnagrafica />} />
              <Route path="/anagrafiche" element={<Anagrafiche />} />
              <Route path="/anagrafiche/cestino" element={<Private requireElevated><AnagraficaCestino /></Private>} />
              <Route path="/anagrafiche/:id" element={<AnagraficaDetail />} />
              <Route path="/companies" element={<Private requireElevated><Companies /></Private>} />
              <Route path="/companies/:id" element={<Private requireElevated><CompanyDetail /></Private>} />
              <Route path="/users" element={<Private requireElevated><Users /></Private>} />
              <Route path="/users/:id" element={<Private requireElevated><UserDetail /></Private>} />
              <Route path="/admin/migration-review" element={<Private requireElevated><MigrationReview /></Private>} />
              <Route path="/settings" element={<Private consenti={vedeImpostazioni}><SettingsHub /></Private>} />
              <Route path="/settings/passaggio-proprieta" element={<Private consenti={(u) => isElevated(u) || haFlag(u, FLAG_PASSAGGIO)}><PassaggioProprietaSettings /></Private>} />
              <Route path="/settings/elenchi-database" element={<Private requireElevated><ElenchiDatabaseSettings /></Private>} />
              <Route path="/admin/backup" element={<Private requireElevated><BackupPlaceholder /></Private>} />
              <Route path="/admin/audit-log" element={<Private requireElevated><AuditLogPlaceholder /></Private>} />
              <Route path="/admin/deletion-requests" element={<Private requireElevated><DeletionRequests /></Private>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
